import { useCallback, useId } from 'react';
import {
  type CollectionAdminItem,
  type HomeBlock,
  type HomeBlockType,
  isCollectionBlock,
} from '@bailafm/shared';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { CollectionCombobox } from '@/components/home/CollectionCombobox';
import { cn } from '@/lib/utils';

const BLOCK_LABELS: Record<HomeBlockType, string> = {
  featuredHero: 'Hero destacado',
  cards: 'Tarjetas',
  featuredCollection: 'Colección destacada',
  artists: 'Artistas destacados',
  cities: 'Elige tu ciudad',
};

// Tipos de colección (los que necesitan selector de colección)
const COLLECTION_TYPES: HomeBlockType[] = ['featuredHero', 'cards', 'featuredCollection'];

// Genera un id único para nuevos bloques
let blockIdCounter = 0;
function nextBlockId(): string {
  blockIdCounter += 1;
  return `block-${Date.now()}-${blockIdCounter}`;
}

function isStaticType(type: HomeBlockType): boolean {
  return type === 'artists' || type === 'cities';
}

// Los bloques de colección llevan `collectionId`; los estáticos no.
function getCollectionId(block: HomeBlock): string | undefined {
  return isCollectionBlock(block) ? block.collectionId : undefined;
}

// Algún bloque de colección del draft no tiene collectionId asignado
// todavía — usado tanto aquí como en DestacadosPage para deshabilitar
// el guardado.
export function hasEmptyCollectionBlocks(blocks: HomeBlock[]): boolean {
  return blocks.some((b) => isCollectionBlock(b) && !b.collectionId);
}

interface HomeBlocksEditorProps {
  blocks: HomeBlock[];
  collections: CollectionAdminItem[];
  onChange(blocks: HomeBlock[]): void;
}

export function HomeBlocksEditor({ blocks, collections, onChange }: HomeBlocksEditorProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = blocks.findIndex((b) => b.id === active.id);
      const newIndex = blocks.findIndex((b) => b.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      onChange(arrayMove(blocks, oldIndex, newIndex));
    },
    [blocks, onChange],
  );

  const handleAdd = useCallback(
    (type: HomeBlockType) => {
      const newBlock: HomeBlock = COLLECTION_TYPES.includes(type)
        ? { id: nextBlockId(), type: type as 'featuredHero' | 'cards' | 'featuredCollection', collectionId: '', isActive: true }
        : { id: nextBlockId(), type: type as 'artists' | 'cities', isActive: true };
      onChange([...blocks, newBlock]);
    },
    [blocks, onChange],
  );

  const handleRemove = useCallback(
    (id: string) => {
      onChange(blocks.filter((b) => b.id !== id));
    },
    [blocks, onChange],
  );

  const handleToggleActive = useCallback(
    (id: string, isActive: boolean) => {
      onChange(blocks.map((b) => (b.id === id ? { ...b, isActive } : b)));
    },
    [blocks, onChange],
  );

  const handleCollectionChange = useCallback(
    (id: string, collectionId: string) => {
      onChange(
        blocks.map((b) =>
          b.id === id && isCollectionBlock(b) ? { ...b, collectionId } : b,
        ),
      );
    },
    [blocks, onChange],
  );

  const hasEmptyCollection = hasEmptyCollectionBlocks(blocks);

  const collectionCount = blocks.filter(isCollectionBlock).length;

  return (
    <div className="space-y-4">
      {/* Cabecera con botón de añadir */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {collectionCount === 0
            ? 'Solo están los bloques fijos (artistas y ciudades).'
            : `${collectionCount} bloque(s) de colección configurado(s)`}
        </p>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="outline" size="sm">
              <Plus className="mr-1 size-4" aria-hidden />
              Añadir colección
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {COLLECTION_TYPES.map((type) => (
              <DropdownMenuItem key={type} onSelect={() => handleAdd(type)}>
                {BLOCK_LABELS[type]}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Lista de bloques reordenable */}
      {blocks.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={blocks.map((b) => b.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {blocks.map((block) => (
                <SortableBlockRow
                  key={block.id}
                  block={block}
                  collections={collections}
                  onRemove={handleRemove}
                  onToggleActive={handleToggleActive}
                  onCollectionChange={handleCollectionChange}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {hasEmptyCollection && (
        <p className="text-xs text-amber-600">
          Hay bloques de colección sin asignar. Asígnale una colección o quita el bloque antes de guardar.
        </p>
      )}
    </div>
  );
}

// Fila de un bloque individual — extraída para poder usar useSortable.
interface SortableBlockRowProps {
  block: HomeBlock;
  collections: CollectionAdminItem[];
  onRemove(id: string): void;
  onToggleActive(id: string, isActive: boolean): void;
  onCollectionChange(id: string, collectionId: string): void;
}

function SortableBlockRow({
  block,
  collections,
  onRemove,
  onToggleActive,
  onCollectionChange,
}: SortableBlockRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });
  const switchId = useId();

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex flex-wrap items-center gap-3 rounded-lg border bg-card p-3',
        isDragging && 'opacity-50 shadow-lg',
        !block.isActive && 'opacity-60',
      )}
    >
      {/* Grip para drag */}
      <button
        type="button"
        className="cursor-grab touch-none text-muted-foreground hover:text-foreground"
        {...attributes}
        {...listeners}
        aria-label="Reordenar bloque"
      >
        <GripVertical className="size-4" aria-hidden />
      </button>

      {/* Badge del tipo */}
      <span className="inline-flex items-center gap-1 rounded-md border bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
        {BLOCK_LABELS[block.type]}
        {isStaticType(block.type) && (
          <span className="text-[10px] text-muted-foreground/60">(fijo)</span>
        )}
      </span>

      {/* Selector de colección (solo para bloques de colección) */}
      {isCollectionBlock(block) && (
        <CollectionCombobox
          value={getCollectionId(block) ?? ''}
          collections={collections}
          onChange={(collectionId) => onCollectionChange(block.id, collectionId)}
        />
      )}

      {/* Espaciador flexible */}
      <div className="flex-1 min-w-0" />

      {/* Switch activo/inactivo */}
      <div className="flex items-center gap-2">
        <Switch
          id={switchId}
          checked={block.isActive}
          onCheckedChange={(checked) => onToggleActive(block.id, checked)}
        />
        <Label htmlFor={switchId} className="text-xs text-muted-foreground cursor-pointer">
          {block.isActive ? 'Activo' : 'Inactivo'}
        </Label>
      </div>

      {/* Botón quitar (solo activo para bloques de colección; el hueco se
          reserva siempre para que el switch quede alineado en toda fila) */}
      <button
        type="button"
        onClick={() => onRemove(block.id)}
        disabled={isStaticType(block.type)}
        className={cn(
          'rounded-md p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive',
          isStaticType(block.type) && 'invisible pointer-events-none',
        )}
        aria-label={`Quitar bloque ${BLOCK_LABELS[block.type]}`}
      >
        <X className="size-4" aria-hidden />
      </button>
    </div>
  );
}
